import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /** @type {HTMLElement | undefined} */
  #previewOverlay;

  /**
   * Overlays another variant's featured media on the current slide. Used by the
   * variant picker's swatch hover preview; the slide comes from a prefetched
   * document, so its markup (srcset/sizes) matches what a real selection renders.
   *
   * @param {Element} sourceSlide - The first slide of the prefetched media gallery.
   */
  previewMedia(sourceSlide) {
    // Keep the outgoing preview at full opacity underneath while the new one
    // fades in over it — a true crossfade, with no base image bleed-through.
    const previousOverlay = this.#previewOverlay;
    this.#previewOverlay = undefined;

    const { slideshow } = this.refs;
    const currentSlide = slideshow?.slides?.[slideshow.current];
    if (!currentSlide) {
      previousOverlay?.remove();
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'media-gallery__variant-preview';

    for (const child of Array.from(sourceSlide.children)) {
      overlay.appendChild(child.cloneNode(true));
    }

    for (const image of overlay.querySelectorAll('img')) {
      image.removeAttribute('loading');
    }

    // The clone must not duplicate the real slide's view transition or zoom triggers.
    for (const element of overlay.querySelectorAll('[data-view-transition-type], [on\\:click]')) {
      element.removeAttribute('data-view-transition-type');
      element.removeAttribute('on:click');
    }

    currentSlide.appendChild(overlay);
    this.#previewOverlay = overlay;

    if (previousOverlay) {
      const removePrevious = () => previousOverlay.remove();
      overlay.addEventListener('animationend', removePrevious, { once: true });
      // Fallback for when the fade-in never runs (e.g. reduced motion).
      setTimeout(removePrevious, 500);
    }
  }

  /**
   * Removes the variant preview overlay, if present, fading it out. A new
   * overlay appended meanwhile paints above the fading one, so switching
   * between finishes reads as a crossfade rather than a cut.
   */
  clearPreview() {
    const overlay = this.#previewOverlay;
    this.#previewOverlay = undefined;

    if (!overlay) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      overlay.remove();
      return;
    }

    overlay.classList.add('media-gallery__variant-preview--leaving');
    overlay.addEventListener('animationend', () => overlay.remove(), { once: true });
    // Fallback in case the animation never runs (e.g. display: none mid-flight).
    setTimeout(() => overlay.remove(), 500);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
